import {Howl, Howler} from "howler";
import {BlackjackHand} from "./api/serverResponseClasses/BlackjackHand";
import {BlackjackBet} from "./BlackjackBet";
import {Card} from "./Card";
import {CardAnimation} from "./CardAnimation";
import data from "./configs/data";
import {HandHighlighter} from "./HandHighlighter";
import {HandPosition} from "./HandPosition";
import {LinearMotionWithAcceleration} from "./LinearMotionWithAcceleration";
import {Utils} from "./Utils";
import {WinInfoAnimation} from "./WinInfoAnimation";

export class BlackjackHandsSimulation extends PIXI.Container {

    private readonly CENTER_HAND = {
        NO_SPLIT_POSITION: new PIXI.Point(640, 385),
        SPLIT_1_POSITION_1: new PIXI.Point(500, 485),
        SPLIT_1_POSITION_2: new PIXI.Point(705, 485),
        SPLIT_2_POSITION_1: new PIXI.Point(500, 485),
        SPLIT_2_POSITION_2: new PIXI.Point(500, 300),
        SPLIT_2_POSITION_3: new PIXI.Point(705, 485),
        SPLIT_2_POSITION_4: new PIXI.Point(705, 300),
    };

    private readonly RIGHT_HAND = {
        NO_SPLIT_POSITION: new PIXI.Point(1105, 250),
        SPLIT_1_POSITION_1: new PIXI.Point(1105, 420),
        SPLIT_1_POSITION_2: new PIXI.Point(900, 420),
        SPLIT_2_POSITION_1: new PIXI.Point(1105, 420),
        SPLIT_2_POSITION_2: new PIXI.Point(1105, 240),
        SPLIT_2_POSITION_3: new PIXI.Point(900, 420),
        SPLIT_2_POSITION_4: new PIXI.Point(900, 240),
    };

    private readonly LEFT_HAND = {
        NO_SPLIT_POSITION: new PIXI.Point(177, 250),
        SPLIT_1_POSITION_1: new PIXI.Point(80, 410),
        SPLIT_1_POSITION_2: new PIXI.Point(300, 410),
        SPLIT_2_POSITION_1: new PIXI.Point(80, 410),
        SPLIT_2_POSITION_2: new PIXI.Point(80, 230),
        SPLIT_2_POSITION_3: new PIXI.Point(300, 410),
        SPLIT_2_POSITION_4: new PIXI.Point(300, 230),
    };

    private readonly DEALER_HAND_POSITION = new PIXI.Point(640, 130);
    private readonly WIN_INFO_ANIMATION_POSITION = new PIXI.Point(640, 170);
    private readonly DEALER_COVERED_CARD_POSITION = new PIXI.Point(652, 130);

    private readonly CARD_DISTRIBUTOR_POSITION = new PIXI.Point(1040, 120);

    private blackjackHands: Array<BlackjackHand> = [];
    private dealerCoveredCard: CardAnimation;
    private handHighlighter: HandHighlighter;
    private winInfoAnimation: WinInfoAnimation;

    private chipsOnWinSound: Howl;
    private youWinSound: Howl;
    private checkingForBlackjackSound: Howl;
    private cardsCollectingSound: Howl;
    private splitSound: Howl;

    private linearMotionWithAcceleration: LinearMotionWithAcceleration = new LinearMotionWithAcceleration();

    constructor() {
        super();
        this.createAndAddPlayerRightHands();
        this.createAndAddPlayerCenterHands();
        this.createAndAddPlayerLeftHands();
        this.createAndAddDealerHandAnimation();

        this.createAndAddDealerCoveredCard();
        this.createAndAddWinInfoAnimation();
        this.createAndAddHandHighlighter();

        this.chipsOnWinSound = new Howl({
            src: [data.audios.chipsOnWin],
            volume: 0.8,
        });

        this.youWinSound = new Howl({
            src: [data.audios.youWin],
            volume: 0.75,
        });

        this.checkingForBlackjackSound = new Howl({
            src: [data.audios.checkingForBlackjack],
            volume: 0.75,
        });

        this.cardsCollectingSound = new Howl({
            src: [data.audios.cardsCollecting],
            volume: 0.7,
        });

        this.splitSound = new Howl({
            src: [data.audios.split],
            volume: 0.7,
        });
    }

    public async addCard(card: Card, handPosition: HandPosition,
                         splitOnePosition?: number, splitTwoPosition?: number): Promise<void> {
        const currentHand = this.getHand(handPosition, splitOnePosition, splitTwoPosition);
        if (currentHand.getHandPosition() === HandPosition.DEALER && currentHand.getCards().length === 1) {
            this.dealerCoveredCard.setCardFront(card.getColor(), card.getName());
            await this.dealerCoveredCard.uncoverCard();
            this.dealerCoveredCard.visible = false;
            currentHand.addCard(card);
            return;
        }

        currentHand.hideHandValueCounter();
        await this.playAddingCardFromDeckHolderAnimation(currentHand, card);
        currentHand.addCard(card);
        currentHand.showHandValueCounter();
    }

    public addCardWithoutAnimation(card: Card, handPosition: HandPosition,
                                   splitOnePosition?: number, splitTwoPosition?: number): void {
        const currentHand = this.getHand(handPosition, splitOnePosition, splitTwoPosition);
        currentHand.addCard(card);
    }

    public async drawDealerCoveredCard(animate: boolean = true): Promise<void> {
        const dealerHand = this.getHand(HandPosition.DEALER);
        if (animate) {
            await this.playAddingCardFromDeckHolderAnimation(dealerHand);
        }
        this.dealerCoveredCard.showCoveredCard();
        this.dealerCoveredCard.visible = true;
    }

    public async checkDealerForBlackjack(): Promise<void> {
        this.winInfoAnimation.playCheckingBlackjack();
        this.checkingForBlackjackSound.play();
        await Utils.requestTimeout(500);
        await this.dealerCoveredCard.checkBlackjack();
        this.winInfoAnimation.hide();
    }

    public async split(handPosition: HandPosition, splitOnePosition?: number): Promise<void> {
        let newHandOne: BlackjackHand, newHandTwo: BlackjackHand, card1: Card, card2: Card, splitHandBetAmount: number;
        const currentHand = this.getHand(handPosition, splitOnePosition);
        if (currentHand.getCards().length !== 2 ||
            currentHand.getSplitNumber() === 2 ||
            currentHand.getCards()[0].getValue() !== currentHand.getCards()[1].getValue()) {
            throw new Error("Split not allowed!");
        }

        if (splitOnePosition === undefined) {
            newHandOne = this.getHand(currentHand.getHandPosition(),
                0);
            newHandTwo = this.getHand(currentHand.getHandPosition(),
                1);
        } else {
            newHandOne = this.getHand(currentHand.getHandPosition(),
                currentHand.getSplitOnePosition(), 0);
            newHandTwo = this.getHand(currentHand.getHandPosition(),
                currentHand.getSplitOnePosition(), 1);
        }

        card1 = currentHand.getCards()[0];
        card2 = currentHand.getCards()[1];
        splitHandBetAmount = currentHand.getBetAmount();
        currentHand.clear();

        this.splitSound.play();
        await Promise.all([
            this.playMovingCardAnimation(currentHand.getGlobalPosition(), newHandOne.getGlobalPosition(), card1),
            this.playMovingCardAnimation(currentHand.getGlobalPosition(), newHandTwo.getGlobalPosition(), card2),
        ]);
        newHandOne.addCard(card1);
        newHandOne.setBetAmount(splitHandBetAmount);

        newHandTwo.addCard(card2);
        newHandTwo.setBetAmount(splitHandBetAmount);
    }

    public insure(handPosition: HandPosition): void {
        const currentHand = this.getHand(handPosition);
        currentHand.insure();
    }

    public doubleHand(handPosition: HandPosition): void {
        const currentHand = this.getHand(handPosition);
        currentHand.double();
    }

    public highlight(handPosition: HandPosition, splitOnePosition?: number, splitTwoPosition?: number): void {
        const hand = this.getHand(handPosition, splitOnePosition, splitTwoPosition);
        this.handHighlighter.visible = true;
        this.handHighlighter.position = hand.position;
    }

    public async brailCards(handPosition: HandPosition,
                            splitOnePosition?: number, splitTwoPosition?: number): Promise<void> {
        const hand = this.getHand(handPosition, splitOnePosition, splitTwoPosition);
        await hand.brailCards();
    }

    public async moveHighlighter(handPosition: HandPosition,
                                 splitOnePosition?: number, splitTwoPosition?: number): Promise<void> {
        const hand = this.getHand(handPosition, splitOnePosition, splitTwoPosition);
        this.handHighlighter.visible = true;
        this.handHighlighter.position = hand.position;
    }

    public showHighlighterArrow(): void {
        this.handHighlighter.showArrow();
    }

    public hideHighlighterArrow(): void {
        this.handHighlighter.hideArrow();
    }

    public hideHighlighter(): void {
        this.handHighlighter.visible = false;
    }

    public increaseBetAmount(betAmount: number, handPosition: HandPosition,
                             splitOnePosition?: number, splitTwoPosition?: number): void {
        const hand = this.getHand(handPosition, splitOnePosition, splitTwoPosition);
        hand.setBetAmount(hand.getBetAmount() + betAmount);
    }

    public setBetAmount(betAmount: number, handPosition: HandPosition,
                        splitOnePosition?: number, splitTwoPosition?: number): void {
        const hand = this.getHand(handPosition, splitOnePosition, splitTwoPosition);
        hand.setBetAmount(betAmount);
    }

    public removeHandBet(handPosition: HandPosition,
                         splitOnePosition?: number, splitTwoPosition?: number): void {
        const hand = this.getHand(handPosition, splitOnePosition, splitTwoPosition);
        hand.setBetAmount(0);
    }

    public getBlackJackBets(): Array<BlackjackBet> {
        let hand;
        const blackjackBets: Array<BlackjackBet> = [];
        hand = this.getHand(HandPosition.PLAYER_RIGHT);
        if (hand.getBetAmount() > 0) {
            blackjackBets.push(new BlackjackBet(hand.getBetAmount(), HandPosition.PLAYER_RIGHT));
        }
        hand = this.getHand(HandPosition.PLAYER_CENTER);
        if (hand.getBetAmount() > 0) {
            blackjackBets.push(new BlackjackBet(hand.getBetAmount(), HandPosition.PLAYER_CENTER));
        }
        hand = this.getHand(HandPosition.PLAYER_LEFT);
        if (hand.getBetAmount() > 0) {
            blackjackBets.push(new BlackjackBet(hand.getBetAmount(), HandPosition.PLAYER_LEFT));
        }
        return blackjackBets;
    }

    public getBlackJackBet(handPosition: HandPosition): BlackjackBet {
        const hand = this.getHand(handPosition);
        return new BlackjackBet(hand.getBetAmount(), handPosition);
    }

    public async setWinInfoLabels(): Promise<void> {
        const dealerHand = this.getHand(HandPosition.DEALER);
        let totalWin = 0;
        for (let i = 0; i < this.blackjackHands.length; i++) {
            let winOrPush: string = "";
            let amountWon = 0;
            let betGotBack = false;
            let bjWon = false;

            if (this.blackjackHands[i].getHandPosition() === HandPosition.DEALER) {
                continue;
            }

            if (this.blackjackHands[i].getCards().length === 0) {
                continue;
            }

            if (this.blackjackHands[i].doesBust()) {
                this.blackjackHands[i].setBetAmount(0);
                continue;
            }

            if (dealerHand.isBlackjack()) {
                if (this.blackjackHands[i].isBlackjack()) {
                    amountWon += this.blackjackHands[i].getBetAmount(); // dealer blackjack and player blackjack (push)
                    winOrPush = "Push";
                }
                if (this.blackjackHands[i].isHandInsured()) {
                    amountWon += this.blackjackHands[i].getBetAmount() / 2; // insurance cost is fund back
                    amountWon += this.blackjackHands[i].getBetAmount(); // dealer blackjack and hand insured (insurance)
                    winOrPush = "Win";
                }
            } else {
                if (this.blackjackHands[i].isBlackjack()) {
                    amountWon += 3 * this.blackjackHands[i].getBetAmount() / 2; // player blackjack payout
                    bjWon = true;
                    winOrPush = "Win";
                    if (!betGotBack) {
                        betGotBack = true;
                        amountWon += this.blackjackHands[i].getBetAmount(); // player regains its bet
                    }
                }
                if (((!this.blackjackHands[i].doesBust() &&
                            !dealerHand.doesBust() &&
                            this.blackjackHands[i].getFinalHandValue() > dealerHand.getFinalHandValue()
                        ) ||
                        (
                            !this.blackjackHands[i].doesBust() &&
                            dealerHand.doesBust()
                        )) && !bjWon
                ) {
                    amountWon += this.blackjackHands[i].getBetAmount(); // player wins with dealer
                    winOrPush = "Win";
                    if (this.blackjackHands[i].isDoubled()) {
                        amountWon += 2 * this.blackjackHands[i].getBetAmount();
                    }
                    if (!betGotBack) {
                        betGotBack = true;
                        amountWon += this.blackjackHands[i].getBetAmount(); // player regains its bet if didn't have bj
                    }
                } else if (this.blackjackHands[i].getFinalHandValue() === dealerHand.getFinalHandValue() &&
                    !dealerHand.doesBust() && !this.blackjackHands[i].doesBust()
                    && !bjWon) {
                    winOrPush = "Push";
                    if (this.blackjackHands[i].isDoubled()) {
                        amountWon += this.blackjackHands[i].getBetAmount();
                    }
                    amountWon += this.blackjackHands[i].getBetAmount();
                }
            }

            if (amountWon === 0) {
                this.blackjackHands[i].playNoWinInfoLabel();
                this.blackjackHands[i].setBetAmount(0);
            } else if (winOrPush === "Win") {
                this.blackjackHands[i].setWinOnChipsStacksStacks(amountWon);
                this.chipsOnWinSound.play();
                this.blackjackHands[i].playYouWinInfoLabel(amountWon);
            } else if (winOrPush === "Push") {
                this.blackjackHands[i].setWinOnChipsStacksStacks(amountWon);
                this.chipsOnWinSound.play();
                this.blackjackHands[i].playPushInfoLabel(amountWon);
            }
            totalWin += amountWon;
        }

        if (totalWin > 0) {
            this.youWinSound.play();
        }
    }

    public async setWinningChipsStacks(): Promise<void> {
        const dealerHand = this.getHand(HandPosition.DEALER);

        for (let i = 0; i < this.blackjackHands.length; i++) {

            if (this.blackjackHands[i].getHandPosition() === HandPosition.DEALER) {
                continue;
            }

            if (this.blackjackHands[i].isHandInsured() && dealerHand.isBlackjack()) {
                continue;
            }

            if (this.blackjackHands[i].isHandInsured() && !dealerHand.isBlackjack()) {
                this.blackjackHands[i].removeInsuranceStack();
            }
        }
    }

    public async clear(): Promise<void> {
        this.handHighlighter.visible = false;
        this.cardsCollectingSound.play();
        for (let i = 0; i < this.blackjackHands.length; i++) {
            this.blackjackHands[i].clear();
        }
    }

    public async removeInsuranceChipsStacks(): Promise<void> {
        for (let i = 0; i < this.blackjackHands.length; i++) {
            this.blackjackHands[i].removeInsuranceStack();
        }
    }

    private async playAddingCardFromDeckHolderAnimation(hand: BlackjackHand, card?: Card): Promise<void> {
        const cardAnimationToMove = new CardAnimation();
        cardAnimationToMove.position = this.CARD_DISTRIBUTOR_POSITION;
        this.addChild(cardAnimationToMove);
        await this.linearMotionWithAcceleration.move(cardAnimationToMove, this.CARD_DISTRIBUTOR_POSITION,
            hand.getNextCardGlobalPosition());
        if (card) {
            cardAnimationToMove.setCardFront(card.getColor(), card.getName());
            await cardAnimationToMove.uncoverCard();
        }
        this.removeChild(cardAnimationToMove);
    }

    private async playMovingCardAnimation(start: PIXI.Point, end: PIXI.Point, card: Card): Promise<void> {
        const cardAnimationToMove = new CardAnimation();
        cardAnimationToMove.position = start;
        cardAnimationToMove.setCardFront(card.getColor(), card.getName());
        cardAnimationToMove.showUncoveredCard();
        this.addChild(cardAnimationToMove);
        await this.linearMotionWithAcceleration.move(cardAnimationToMove, start, end);
        this.removeChild(cardAnimationToMove);
    }

    private getHand(handPosition: HandPosition,
                    splitOnePosition?: number,
                    splitTwoPosition?: number): BlackjackHand {
        for (let i = 0; i < this.blackjackHands.length; i++) {
            if (this.blackjackHands[i].getHandPosition() === handPosition &&
                this.blackjackHands[i].getSplitOnePosition() === splitOnePosition &&
                this.blackjackHands[i].getSplitTwoPosition() === splitTwoPosition) {
                return this.blackjackHands[i];
            }
        }
        throw new Error("Hand not exist! " + handPosition + " " + splitOnePosition + " " + splitTwoPosition);
    }

    private createAndAddDealerHandAnimation(): void {
        const dealerHand = new BlackjackHand(0, HandPosition.DEALER, undefined, undefined, 0);
        dealerHand.position = this.DEALER_HAND_POSITION;
        this.blackjackHands.push(dealerHand);
        this.addChild(dealerHand);
    }

    private createAndAddDealerCoveredCard(): void {
        this.dealerCoveredCard = new CardAnimation();
        this.dealerCoveredCard.showCoveredCard();
        this.dealerCoveredCard.position = this.DEALER_COVERED_CARD_POSITION;
        this.dealerCoveredCard.visible = false;
        this.addChild(this.dealerCoveredCard);
    }

    private createAndAddHandHighlighter(): void {
        this.handHighlighter = new HandHighlighter();
        this.handHighlighter.visible = false;
        this.addChild(this.handHighlighter);
    }

    private createAndAddWinInfoAnimation(): void {
        this.winInfoAnimation = new WinInfoAnimation();
        this.winInfoAnimation.position = this.WIN_INFO_ANIMATION_POSITION;
        this.addChild(this.winInfoAnimation);
    }

    private createAndAddPlayerRightHands(): void {
        let bjHand: BlackjackHand;

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_RIGHT,
            undefined, undefined, 0);
        bjHand.position = this.RIGHT_HAND.NO_SPLIT_POSITION;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_RIGHT,
            0, undefined, 1);
        bjHand.position = this.RIGHT_HAND.SPLIT_1_POSITION_1;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_RIGHT,
            0, 1, 2);
        bjHand.position = this.RIGHT_HAND.SPLIT_2_POSITION_2;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_RIGHT,
            0, 0, 2);
        bjHand.position = this.RIGHT_HAND.SPLIT_2_POSITION_1;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_RIGHT,
            1, 1, 2);
        bjHand.position = this.RIGHT_HAND.SPLIT_2_POSITION_4;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_RIGHT,
            1, 0, 2);
        bjHand.position = this.RIGHT_HAND.SPLIT_2_POSITION_3;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_RIGHT,
            1, undefined, 1);
        bjHand.position = this.RIGHT_HAND.SPLIT_1_POSITION_2;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);
    }

    private createAndAddPlayerCenterHands(): void {
        let bjHand: BlackjackHand;

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_CENTER,
            undefined, undefined, 0);
        bjHand.position = this.CENTER_HAND.NO_SPLIT_POSITION;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_CENTER,
            0, undefined, 1);
        bjHand.position = this.CENTER_HAND.SPLIT_1_POSITION_1;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_CENTER,
            0, 1, 2);
        bjHand.position = this.CENTER_HAND.SPLIT_2_POSITION_2;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_CENTER,
            0, 0, 2);
        bjHand.position = this.CENTER_HAND.SPLIT_2_POSITION_1;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_CENTER,
            1, 1, 2);
        bjHand.position = this.CENTER_HAND.SPLIT_2_POSITION_4;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_CENTER,
            1, 0, 2);
        bjHand.position = this.CENTER_HAND.SPLIT_2_POSITION_3;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_CENTER,
            1, undefined, 1);
        bjHand.position = this.CENTER_HAND.SPLIT_1_POSITION_2;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);
    }

    private createAndAddPlayerLeftHands(): void {
        let bjHand: BlackjackHand;

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_LEFT,
            undefined, undefined, 0);
        bjHand.position = this.LEFT_HAND.NO_SPLIT_POSITION;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_LEFT,
            0, undefined, 1);
        bjHand.position = this.LEFT_HAND.SPLIT_1_POSITION_1;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_LEFT,
            0, 1, 2);
        bjHand.position = this.LEFT_HAND.SPLIT_2_POSITION_2;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_LEFT,
            0, 0, 2);
        bjHand.position = this.LEFT_HAND.SPLIT_2_POSITION_1;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_LEFT,
            1, 1, 2);
        bjHand.position = this.LEFT_HAND.SPLIT_2_POSITION_4;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_LEFT,
            1, 0, 2);
        bjHand.position = this.LEFT_HAND.SPLIT_2_POSITION_3;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);

        bjHand = new BlackjackHand(0, HandPosition.PLAYER_LEFT,
            1, undefined, 1);
        bjHand.position = this.LEFT_HAND.SPLIT_1_POSITION_2;
        this.blackjackHands.push(bjHand);
        this.addChild(bjHand);
    }
}



// WEBPACK FOOTER //
// ./src/app/BlackjackHandsSimulation.ts